import type { User } from "@/store/types";
import { getBookingStatus } from "@/utils/Formatting";
import { pb } from "@/utils/PocketBaseAdapter";
import { moment } from "@/utils/useTimeZone";
import { useListStore } from "./listStore";
import { useRoute } from "vue-router";


export const useCalendarStore = defineStore("calendar", () => {
  // store
  const list = useListStore();

  const { getListArea, fetchActivities: fetchActivityList } = list;

  const { listArea: areasList, activityList } = storeToRefs(list);

  const route = useRoute()
  // data
  const bookingList = ref<any>();
  const availableCalendars = ref<any>([]);
  const selectedCalendars = ref<any>([]);
  const isBookingStepperVisible = ref<boolean>(false);
  const isSimplerBookingStepperVisible = ref<boolean>(false);
  const isConfirmEditBookingDialog = ref<boolean>(false);
  const editBookingData = ref<any>("");
  const usersList = ref<User[]>([]);
  const areaList = ref<any[]>([]);

  const bookingStatusList = ref([
    {
      color: "grey",
      name: "Show Completed",
      value: "completed",
    },
    {
      color: "green",
      name: "Show In Progress",
      value: "inprogress",
    },
  ]);

  const selectedBookingStatus = ref<any>([]);
  const currentViewType = ref<string>("");
  const calendarUpdateKey = ref<number>(1);
  const visibleStartDate = ref<any>();
  const recurringDetails = ref<any>({});
  const visibleRange = ref<any>({});

  // computed
  const getBookingList = computed(() => {
    // Filter booking based on selected calendars
    const filteredBookings =
      bookingList.value?.filter((el: any) =>
        selectedCalendars?.value?.includes(el.activity),
      ) || [];

    // Further filter based on view type and status
    if (currentViewType.value === "listMonth") {
      const today = moment().startOf("day"); // Start of today

      // Filter bookings that start today or in the future
      const filteredByDate = filteredBookings.filter((el: any) => {
        const startDate = moment(el.calendar.startDate).startOf("day"); // Start of booking's startDate

        return startDate.isSameOrAfter(today); // Include today or future dates
      });

      // Further filter based on selected booking status
      if (selectedBookingStatus.value.length) {
        return filteredByDate.filter(
          (el: any) =>
            selectedBookingStatus.value.includes(el.calendar.status) ||
            el.calendar.status === "futureBooking",
        );
      }

      return filteredByDate?.filter(
        (booking: any) => booking?.calendar?.status === "futureBooking",
      );
    }

    // Filter by selected booking status for other views
    if (selectedBookingStatus.value.length) {
      return filteredBookings.filter(
        (el: any) =>
          selectedBookingStatus.value.includes(el.calendar.status) ||
          el.calendar.status === "futureBooking",
      );
    }

    // no selection on side status
    return filteredBookings?.filter(
      (booking: any) => booking?.calendar?.status === "futureBooking",
    );
  });

  // Methods
  const fetchBookings = async () => {
    try {
      const objectMap = availableCalendars.value.reduce(
        (acc: any, item: any) => {
          acc[item.id] = item;

          return acc;
        },
        {},
      );

      // Activity list of selected location
      const activitiesList = availableCalendars.value?.map(
        (el: any) => `activity="${el.id}"`,
      );

      const filter = `(${activitiesList.join(" || ")}) && startTime <= '${
        visibleRange.value.endDate
      }' && endTime >= '${visibleRange.value.startDate}' && state != 'Deleted'`;

      const bookings = await pb.collection("bookings").getFullList({
        filter,
        expand: "users,area,activity,docketLine",
      });

      if (bookings) {
        const bookingIds = bookings.map((booking) => booking.id);

        const [recurringBookingsList, eventsList] = bookingIds.length
          ? await Promise.all([
              pb.collection("recurringBookings").getFullList({
                filter: bookingIds
                  .map((id) => `bookings ~ '${id}'`)
                  .join(" || "),
              }),
              pb.collection("events").getFullList({
                filter: bookingIds
                  .map((id) => `bookings ~ '${id}'`)
                  .join(" || "),
                expand:
                  "bookings.owner,bookings.docketLine.docket,bookings.area", // Ensure to expand all necessary fields
              }),
            ])
          : [[], []];

        const recurringEventsList = eventsList.length
          ? await pb.collection("recurringEvents").getFullList({
              filter: eventsList
                .map((event) => `events ~ '${event.id}'`)
                .join(" || "),
              expand: "events.bookings.docketLine.docket",
            })
          : [];

        // Map bookings with user names and recurring booking details
        bookings.map((el) => {
          // Create user names
          const userNames =
            el.expand?.users?.map(
              (user: any) => `${user.firstName} ${user.lastName}`,
            ) || [];

          el.title = userNames.join(", ");
          el.start = el.startTime;
          el.end = moment(el.startTime)
            .clone()
            .add(el.duration, "minutes")
            .toISOString();
          el.calendar = {
            eventColor: objectMap[el?.activity]?.color,
            activityName: objectMap[el?.activity]?.name,
            due: el.due,
            id: el.id,
            startDate: el.startTime,
            endTime: moment(el.startTime)
              .add(el.duration, "minutes")
              .toISOString(),
            duration: el.duration,
            statusClass: getBookingStatus(
              el.actualStartTime,
              el.actualDuration,
            ),
            status: getBookingStatus(el.actualStartTime, el.actualDuration),
          };

          // Check for recurring bookings
          const matchedRecurring = recurringBookingsList?.find((recurring) =>
            recurring?.bookings?.includes(el.id),
          );

          el.calendar.recurringBookingDetails = matchedRecurring || {};

          // Check for event details
          const matchedEvent = eventsList?.find((event) =>
            event?.bookings?.includes(el.id),
          );

          el.calendar.eventDetails = matchedEvent || {};

          // Check for recurring event details within the expanded events
          const matchedRecurringEvent = recurringEventsList?.find(
            (recurringEvent) =>
              recurringEvent.expand?.events?.some((event: any) =>
                event.expand?.bookings?.some(
                  (booking: any) => booking.id === el.id,
                ),
              ),
          );

          el.calendar.recurringEventDetails = matchedRecurringEvent || {};
        });

        bookingList.value = bookings;

        // Trigger calendar update after a delay
        setTimeout(() => {
          calendarUpdateKey.value++;
        }, 2000);
      }
    } catch (e) {}
  };

  const fetchActivities = async () => {
    try {
      await fetchActivityList();
      availableCalendars.value = activityList.value;
      selectedCalendars.value = availableCalendars.value.map(
        (el: any) => el.id,
      );

      return activityList.value;
    } catch (e) {}
  };

  const getUserList = async () => {
    try {
      if (usersList.value.length) return;

      const records = await pb.collection("users").getFullList({
        fields:
          "id,name,email,firstName,lastName,avatar,username,leftHanded,tempBalance,mobileNumber",
        filter: 'status != "Suspended"',
      });

      if (records) usersList.value = records;
    } catch (e) {}
  };

  const getAreasList = async () => {
    try {
      await getListArea();
      areaList.value = areasList.value;
    } catch (e) {}
  };

  const deleteBooking = async ({
    bookingId,
    recurringId,
    isAll,
  }: {
    bookingId: string;
    recurringId?: string;
    isAll: boolean;
  }) => {

    if (!bookingId && !recurringId) return;
    const currentDate = new Date();
    try {
      const processBooking = async (booking: any) => {
        const bookingDate = moment(booking.startTime).utc();
        const currentDate = moment().utc();

        // Check if currentDate is after bookingDate
        if (booking.docketLine) {
          try {
            let bookingOptions: any = await pb.collection("bookings").getOne(booking.id, {expand: 'docketLine'})
            // get Docket
            if (bookingOptions?.expand?.docketLine?.docket) {
              let docketLines = await pb.collection("docketLines").getFullList({filter: 'docket="' + bookingOptions.expand.docketLine.docket + '"'})
              if (docketLines && bookingOptions?.options) {
                for (const dLine of docketLines) {
                  if (dLine?.product && bookingOptions.options.includes(dLine.product)) {
                    // delete Docket Line
                    try {
                      await pb.collection("docketLines").delete(dLine.id)
                    } catch (e) {
                    }
                  }
                }
              }
            }
          }catch(e){}

          await pb.collection("bookings").update(booking.id, {
            state: "Deleted",
            value: 0,
            options:[],
          });
        } else {
          // await pb.collection("bookings").delete(booking.id);
          await pb.collection("bookings").update(booking.id, {
            state: "Deleted",
            value: 0,
            options:[],
          });
        }

        // Get All Dockets Used on the Booking And Credit Back to that user - with the same expiry
        try {
          let docketLines = await pb.collection("docketLines").getFullList({filter: 'booking="' + booking.id + '"'})
          let docketMap = docketLines.map(dl => 'docket="' + dl.docket + '"').join('||')
          let payments = await pb.collection("docketPayments").getFullList({filter: 'paymentType="Coupon" && (' + docketMap + ')', expand:"coupon, docket"})
          if(payments){
            for(const payment of payments){
              if(payment?.expand?.coupon && payment?.amount > 0){
                let expiry = payment?.expand?.coupon.expiryTime;
                let qty = payment?.couponQty > 0 ? payment?.couponQty : 1
                let amount = payment?.amount || 0
                // Create New Coupon for User
                // Update Quantity Remaining
                const couponData = await pb.collection("coupons").create({
                  name: "Coupon Booking Refund",
                  qty: Math.floor(qty),
                  activity: "",
                  qtyRemaining: Math.floor(qty),
                  expiryTime: expiry,
                  validFrom:payment?.expand?.coupon?.validFrom || '',
                  expired: false,
                  status:"Active",
                  user: payment?.expand?.coupon?.user, // Give it To the New User
                });

                // 2 - CREATE NEW DOCKET for the user getting the coupon
                let docket = await pb.collection('dockets').create({
                  user: payment?.expand?.coupon?.user,
                  location: payment?.expand?.docket?.location,
                  status: "Open",
                  due: 0,
                  total: amount * -1,
                  createdBy: payment?.expand?.docket?.createdBy,
                  source: "Booking",
                })

                // 3 - CREATE DOCKET LINE - Joins the coupons to a docket - docketLines Table
                let docketLineToMake = {
                  couponQty: Math.floor(qty * -1),
                  coupon: couponData.id,
                  docket: docket.id,
                  updatedBy: payment?.expand?.docket?.createdBy,
                  transactionType: 'Booking Payment',
                  qty: Math.floor(qty * -1),
                  itemPrice: parseFloat(amount / Math.floor(qty)).toFixed(2), // Docket Value
                  discount: 0,
                  lineTotal: amount * -1, // Docket Value
                  status: 'Available',
                  booking: booking.id,
                  createdBy: payment?.expand?.docket?.createdBy,
                  lineText: "Coupon Refunded for Cancelled Booking"
                };

                await pb.collection("docketLines").create(docketLineToMake);

                let finalCouponForDocketPayment = {
                  docket: docket.id,
                  paymentType: "Coupon",
                  status: "Approved",
                  amount: amount * -1,
                  user: payment?.expand?.coupon?.user,
                  coupon: couponData.id,
                  couponQty: Math.floor(qty * -1),
                  notes: "Coupon Refund for Cancelled Booking"
                }

                await pb.collection("docketPayments").create(finalCouponForDocketPayment)
                await pb.collection('dockets').update(docket.id,{status:"Paid"})
              }
            }
          }
        } catch(e){}

        // Credit Back to users Account the Amount of the booking
        try {
          let docketLines = await pb.collection("docketLines").getFullList({filter: 'booking="' + booking.id + '"'})
          let docketMap = docketLines.map(dl => 'docket="' + dl.docket + '"').join('||')
          let payments = await pb.collection("docketPayments").getFullList({
            filter: 'paymentType="User Account" && (' + docketMap + ')',
            expand: "docket"
          })
          if (payments) {
            for (const payment of payments) {
              if (payment?.amount > 0) {
                let amount = payment?.amount || 0

                // 2 - CREATE NEW DOCKET for the user getting the Account Credit
                let docket = await pb.collection('dockets').create({
                  user: payment?.user,
                  location: payment?.expand?.docket?.location,
                  status: "Open",
                  due: 0,
                  total: amount * -1,
                  createdBy: payment?.expand?.docket?.createdBy,
                  source: "Booking",
                })

                // 3 - CREATE DOCKET LINE - Joins the coupons to a docket - docketLines Table
                let docketLineToMake = {
                  docket: docket.id,
                  updatedBy: payment?.expand?.docket?.createdBy,
                  transactionType: 'Booking Payment',
                  qty: -1,
                  itemPrice: amount, // Docket Value
                  discount: 0,
                  lineTotal: amount * -1, // Docket Value
                  status: 'Available',
                  booking: booking.id,
                  createdBy: payment?.expand?.docket?.createdBy,
                  lineText: "Account Refunded for Cancelled Booking"
                };

                await pb.collection("docketLines").create(docketLineToMake);

                let finalCouponForDocketPayment = {
                  docket: docket.id,
                  paymentType: "User Account",
                  status: "Approved",
                  amount: amount * -1,
                  user: payment?.user,
                  notes: "Account Refund for Cancelled Booking"
                }

                await pb.collection("docketPayments").create(finalCouponForDocketPayment)
                await pb.collection('dockets').update(docket.id, {status: "Paid"})
              }
            }
          }
        } catch(e){}
      };

      try {
        if (recurringId && isAll) {
          const recurringBooking = await pb
            .collection("recurringBookings")
            .getOne(recurringId, { expand: "bookings" });

          const bookings = recurringBooking?.expand?.bookings || [];

          await Promise.all(bookings.map(processBooking));
        } else {
          const { docketLine, startTime, id } = await pb
            .collection("bookings")
            .getOne(bookingId);

          const bookingDate = new Date(startTime);

          await processBooking({ id, docketLine, startTime });
        }
      } catch (error) {
        console.log("Error in if-else block:", error);
      }

      // Trigger calendar update after deletion
      setTimeout(() => {
        calendarUpdateKey.value++;
      }, 2000);

      isBookingStepperVisible.value = false;

    } catch (error) {
      console.error("Caught error in deleteBooking:", error);
      throw error;
    }

    if(route.name == 'pos'){
      window.location = window.location
    }
  };

  const updateVisibleDateRange = (start: any, end: any) => {
    visibleRange.value = {
      startDate: moment(start)
        .startOf("day")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss"), // start of the day
      endDate: moment(end).endOf("day").utc().format("YYYY-MM-DD HH:mm:ss"), // end of the day
    };
  };

  // Hooks
  onMounted(async () => {
    await getUserList();
  });

  return {
    availableCalendars,
    selectedCalendars,
    getBookingList,
    editBookingData,
    usersList,
    areaList,
    bookingStatusList,
    selectedBookingStatus,
    currentViewType,
    calendarUpdateKey,
    visibleStartDate,
    isBookingStepperVisible,
    isSimplerBookingStepperVisible,
    recurringDetails,
    isConfirmEditBookingDialog,
    visibleRange,
    deleteBooking,
    fetchBookings,
    getAreasList,
    fetchActivities,
    updateVisibleDateRange,
    getUserList,
  };
});
